import * as React from "react";
import { Modal, ModalSize, ModalBody, ModalFooter, Form, AlertType, CustomButton, ButtonType, CustomSubmitButton, HiddenInput, Textarea, CustomDatePicker, Checkbox } from "soprasteria-ui-library";
import { useAuth } from "../../../../App";
import { PatientContactedTreatmentApi } from "../../../../api/PatientContactedTreatmentApi";
import { IAddOrUpdatePatientContactedTreatment, IPatientContactedTreatmentListItem } from "../../../../models/IPatientContactedTreatment";
import { setMessage } from "../../../../helpers/MessageHelper";
import { addToMonth, dateTimeToDateString } from "../../../../helpers/DateHelper";
import { IPatientTreatment } from "../../../../models/IPatientTreatment";
import { TreatmentsDropdown } from "../../TreatmentReport/TreatmentsDropdown";

interface IPatientContactedTreatmentModal {
    patientId: number;
    patientContactedTreatmentToEdit?: IPatientContactedTreatmentListItem;
    onSave: () => any;
    onCloseModal: () => void;
}

export const PatientContactedTreatmentModal: React.FunctionComponent<IPatientContactedTreatmentModal> = ({ patientId, patientContactedTreatmentToEdit, onSave, onCloseModal }) => {
    const auth = useAuth();
    const api = new PatientContactedTreatmentApi(auth);
    
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [treatmentCodes, setTreatmentCodes] = React.useState<IPatientTreatment[]>();
    const d = new Date();

    React.useEffect(() => {
        getTreatmentCodes();
        document.getElementById("open-patient-contacted-treatment-modal")?.click();
    }, [])

    const getTreatmentCodes = () => {
        api.getTreatmentCodes((data) => setTreatmentCodes(data));
    }

    const submitPatientContactedTreatment = (patientContactedTreatment: IAddOrUpdatePatientContactedTreatment) => {
        setIsSubmitting(true);
        patientContactedTreatment.patientId = patientId;
        api.savePatientContactedTreatment(patientContactedTreatment, () => {
            document.getElementById("close-patient-contacted-treatment-modal")?.click();
            onSave();
        }, (message) => {
            const newErrorMessage = setMessage(message, errorMessage);
            setErrorMessage(newErrorMessage);
            setIsSubmitting(false);
        });
    }

    const getHeader = () => {
        if (patientContactedTreatmentToEdit) {
            return patientContactedTreatmentToEdit.canBeEdited ? "Redigera rapport om kontaktad patient" : "Visa rapport om kontaktad patient";
        } else {
            return "Skapa rapport om kontaktad patient";
        }
    }

    return (
        <Modal id="patient-contacted-treatment-modal" header={getHeader()} modalSize={ModalSize.large}>
            <Form onSubmit={submitPatientContactedTreatment} >
                <ModalBody>
                    <div className="row">
                        <div className="col-12">
                            <CustomDatePicker
                                name="contactedDate"
                                label="Datum"
                                min={addToMonth(d, -3)}
                                max={d}
                                value={patientContactedTreatmentToEdit ? new Date(dateTimeToDateString(patientContactedTreatmentToEdit.contactedDate)) : undefined}
                                inlineLabel
                                required
                                disabled={patientContactedTreatmentToEdit ? !patientContactedTreatmentToEdit.canBeEdited : false}
                            />
                            <TreatmentsDropdown label="Åtgärdskod"
                                treatments={treatmentCodes ?? []}
                                name="treatmentCodeId"
                                isLoading={treatmentCodes === undefined}
                                defaultValue={patientContactedTreatmentToEdit ? patientContactedTreatmentToEdit.treatmentCodeId.toString() : undefined}
                                required={true}
                                disabled={patientContactedTreatmentToEdit ? !patientContactedTreatmentToEdit.canBeEdited : false}
                             />
                            <Textarea label="Kommentar"
                                name="comment"
                                defaultValue={patientContactedTreatmentToEdit?.comment}
                                maxLength={5000}
                                rows={6}
                                inlineLabel
                                required
                                readonly={patientContactedTreatmentToEdit ? !patientContactedTreatmentToEdit.canBeEdited : false}
                            />
                        </div>
                    </div>                    
                    <HiddenInput name="patientContactedTreatmentId" value={patientContactedTreatmentToEdit ? patientContactedTreatmentToEdit.patientContactedTreatmentId!.toString() : ""} />
                </ModalBody>
                <ModalFooter>
                    <div className="text-right btn-group" role="group">
                        <CustomButton
                            buttonType={ButtonType.general}
                            buttonText="Avbryt"
                            data-dismiss="modal"
                            id="close-patient-contacted-treatment-modal"
                            onClick={() => onCloseModal()} />
                        <CustomSubmitButton isLoading={isSubmitting} disabled={patientContactedTreatmentToEdit ? !patientContactedTreatmentToEdit.canBeEdited : false} >Spara</CustomSubmitButton>
                    </div>
                    <input type="hidden" data-toggle="modal" id="open-patient-contacted-treatment-modal" data-target="#patient-contacted-treatment-modal" />
                </ModalFooter>
            </Form>
        </Modal>
    );
}